import { db } from '../core/db.js';
import { compress } from '../projects/compress.js';
import { beadCloud } from '../core/azure.js';
import { utils, HiddenCanvas } from '../utils.js';

export const doBackup = () => {
    let zip = new JSZip();

    db.projects.each(file => {
        zip.file(file.title + '.bead', JSON.stringify(file))
    }).then(result => {
        zip.generateAsync({type:'blob'})
        .then(blob => {
            saveAs(blob, 'archive.zip');
        })
        .catch(err => {
            console.error(err);
        });
    })
    .catch(err => {
        console.error(err);
    });
};

const migrateDate = obj => {
    const date = typeof obj?.number === 'number' ?
        new Date(obj.number):
        null;

    return utils.YYYYMMDD(date);
};

const migrateIgnoreLastRow = file => {
    if (file.settings.pattern.slice(0, 6) !== 'peyote') return false;
    return file.settings.displayRows === (file.settings.columns * 2) - 1;
};

const migrateWidth = file => {

    const beadHeight = file.settings.beadHeight || 46;
    const diameter = file.settings.diameter || 1.6;

    return Number(
        ((beadHeight / 60) * diameter).toFixed(2)
    );
};

const migrateThumbnail = thumbnail => {
    return new Promise((resolve, reject) => {
        const hidden = new HiddenCanvas(110, 70);
        hidden.ctx.fillStyle = "#FFF";
        hidden.ctx.fillRect(0, 0, 110, 70);
        const image = new Image;
        image.onload = () => {
            hidden.ctx.drawImage(image, 0, 0, 220, 140, 0, 0, 110, 70);
            const result = hidden.canvas.toDataURL('image/jpeg', 0.5);
            resolve(result);
            hidden.remove();
            image.remove();
        };
        image.onerror = reject;
        image.src = thumbnail;
    });
};

const getLibraryIndex = file => {
    if (typeof file.settings.library?.index === 'number') return file.settings.library.index;
    if (!file.settings.beadShape) return 1;

    const myBeadShape = [
        { beadShape: 'round', index: 1 },
        { beadShape: 'cylinder', index: 5 },
        { beadShape: 'plastic', index: 13 },
    ].find(element => element.beadShape === file.settings.beadShape);

    if (!myBeadShape) return 1;

    return myBeadShape.index;
};

const dedupe = swatchArr => {
    const result = [];

    swatchArr.forEach(element => {
        if (result.some(entry => (
            entry.color === element.color &&
            entry.description === element.description &&
            entry.finishName === element.finishName
        ))) return;
        result.push(element);
    });

    return result;
};

const processSwatchFinish = finishName => {
    const allowedNames = [
        'matte', 'semigloss', 'gloss', 'cylinder_matte',
        'cylinder_semigloss', 'cylinder_gloss', 'plastic'
    ];
    if (allowedNames.some(n => n === finishName)) return finishName;
    if (finishName.includes('plastic')) return 'plastic';
    if (finishName.includes('cylinder')) return 'cylinder_matte';
    return 'matte';
};

const migrateSwatch = ({beads, swatch}) => {
    const oldVersion = swatch.some(element => element.active);
    if (!oldVersion) return swatch;

    const swatchesFromBeads = beads.map(element => ({
        color: element.fillColor,
        finishName: processSwatchFinish(element.finishName),
        inner: element.inner || 'opaque',
        description: element.description || 'Custom swatch',
        secondColor: element.secondColor || null,
        letterCode: element.letterCode || '',
    }));

    const swatchesFromSwatch = swatch
        .filter(element => element.active)
        .map(element => ({
            color: element.color,
            finishName: processSwatchFinish(element.finishName),
            inner: element.inner || 'opaque',
            description: element.description || 'Custom swatch',
            secondColor: element.secondColor || null,
            letterCode: element.letterCode || '',
        }))

    const combinedSwatches = swatchesFromBeads.concat(swatchesFromSwatch);

    const uniqueSwatches = dedupe(combinedSwatches);

    return uniqueSwatches
        .map((element, i) => ({
            ...element,
            id: i
        }));
};

const migrateBeads = ({beads, swatch}) => {
    const oldVersion = beads.some(element => element.active);
    if (!oldVersion) return beads;

    return beads.map(element => {

        let mySwatch;
        mySwatch = swatch.find(entry => entry.description === element.description);
        if (!mySwatch) {
            mySwatch = swatch.find(entry => entry.color === element.fillColor);
        }

        if (!mySwatch) return;

        return {
            row: element.row,
            column: element.column,
            style: mySwatch.id
        }
    }).filter(e => !!e);
};

export const migrateProject = async file => {
    if (file.version === 3) {
        file.userId = currentUserId;
        return file;
    }
    if (!file.title) return;
    if (file.type === 'palette') return;
    const beadArr = file.beads || [];

    const libraryIndex = getLibraryIndex(file);
    const swatch = migrateSwatch({beads: beadArr, swatch: file.swatch});
    const _beads = migrateBeads({beads: beadArr, swatch});

    const result = {
        title: file.title,
        lowerTitle: file.title.toLowerCase(),
        description: '',
        instructions: '',
        credits: '',
        tags: [],
        beadCount: beadArr.length,
        dateCreated: migrateDate(file.dateCreated),
        dateModified: migrateDate(file.dateModified),
        settings: {
            rows: file.settings.rows,
            columns: file.settings.columns,
            ignoreLastRow: migrateIgnoreLastRow(file),
            beadShape: file.settings.beadShape,
            beadDisplayHeight: file.settings.beadHeight || 46,
            diameter: file.settings.diameter || 1.6,
            width: migrateWidth(file),
            patternName: file.settings.pattern,
            offset: file.settings.offset?.join('') || '',
            libraryIndex
        },
        beadString: compress(_beads),
        swatch,
        camera: file.camera,
        thumbnail: await migrateThumbnail(file.thumbnail),
        userId: currentUserId,
        version: 3
    };

    return result;
};

const getProjects = async () => {
    const myProjects = await db.projects.toArray();

    const result = [];
    for (let index in myProjects) {
        const migratedProject = await migrateProject(myProjects[index]);
        if (migratedProject) result.push(migratedProject);
    }
    return result;
};

const updateSettings = async () => {
    const legacySettings = await db.settings.get(1);
    legacySettings.hasMigrated = true;
    await db.settings.update(1, legacySettings);
};

b3.resetMigration = async () => {
    const legacySettings = await db.settings.get(1);
    legacySettings.hasMigrated = false;
    await db.settings.update(1, legacySettings);
};

b3.setMigration = async () => {
    const legacySettings = await db.settings.get(1);
    legacySettings.hasMigrated = true;
    await db.settings.update(1, legacySettings);
};

const addToLog = str => {
    const myArr = b3.store.state.upgradeLog;
    myArr.push(str);
    b3.store.mySet('upgradeLog', myArr);
};

// warn user that migration will happen and ask if want to backup first
export const migrate = async () => {
    addToLog('Starting update');
    b3.willMigrate = true;
    b3.store.mySet('currentTab', 'Upgrade');
    b3.store.mySet('currentScreen', 'menu');

    // get projects from local db
    addToLog('Getting saved projects')
    const projects = await getProjects();

    addToLog(`Found ${projects.length} saved projects`);
    // load projects to cloud
    addToLog('Loading projects to cloud');
    await beadCloud({
        fn: 'saveMultipleToCloud',
        params: {
            recordArray: projects,
            container: 'projects'
        }
    });
    addToLog('Verifying projects in cloud');

    const { count } = await beadCloud({fn: 'getProjectCount', params: {currentUserId}});

    addToLog(`${count} projects found in cloud`);

    if (count === 0 && projects.length) {
        addToLog('No projects were successfully uploaded. Please try again or ask for help.');
        return;
    }

    addToLog('Finishing up');

    // set hasMigrated to true on local db settings
    await updateSettings();
    addToLog('Update complete. Please refresh your browser.');
};
